"use client";

import { c as _c } from "react/compiler-runtime";
import { clsx } from "@/helpers/clsx";
import { useMaxBreakpoint } from "@/helpers/useBreakpoint";
import { AnimatePresence, motion } from "framer-motion";
import React, { useRef, useState } from "react";
interface LensProps {
  children: React.ReactNode;
  zoomFactor?: number;
  lensSize?: number;
  position?: {
    x: number;
    y: number;
  };
  isStatic?: boolean;
  isFocusing?: () => void;
  hovering?: boolean;
  setHovering?: (hovering: boolean) => void;
  className?: string;
}
export const Lens: React.FC<LensProps> = t0 => {
  const $ = _c(37);
  let children;
  let className;
  let hovering;
  let props;
  let setHovering;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      children,
      zoomFactor: t1,
      lensSize: t2,
      isStatic: t3,
      position: t4,
      hovering,
      setHovering,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = hovering;
    $[4] = props;
    $[5] = setHovering;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    children = $[1];
    className = $[2];
    hovering = $[3];
    props = $[4];
    setHovering = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  const zoomFactor = t1 === undefined ? 1.4 : t1;
  const lensSize = t2 === undefined ? 170 : t2;
  const isStatic = t3 === undefined ? false : t3;
  let t5;
  if ($[10] !== t4) {
    t5 = t4 === undefined ? {
      x: 0,
      y: 0
    } : t4;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const position = t5;
  const isMobile = useMaxBreakpoint("sm");
  const zoom = zoomFactor + (isMobile ? 1 : 0);
  const lens = lensSize * (isMobile ? 0.7 : 1.2);
  const containerRef = useRef(null);
  const [localIsHovering, setLocalIsHovering] = useState(false);
  const isHovering = hovering !== undefined ? hovering : localIsHovering;
  const setIsHovering = setHovering || setLocalIsHovering;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      x: 100,
      y: 100
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const [mousePosition, setMousePosition] = useState(t6);
  let t7;
  if ($[13] !== setIsHovering) {
    t7 = e => {
      setIsHovering(true);
      const rect = e.currentTarget.getBoundingClientRect();
      let event = e as any;
      const isTouch = "touches" in e || "sourceCapabilities" in e.nativeEvent && (e.nativeEvent.sourceCapabilities as any)?.firesTouchEvents;
      if ("touches" in e) {
        event = e.touches[0];
        if (!event) {
          return;
        }
      }
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top - (isTouch ? 70 : 0);
      setMousePosition({
        x,
        y
      });
    };
    $[13] = setIsHovering;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleMouseMove = t7;
  let t8;
  if ($[15] !== className) {
    t8 = clsx("relative select-none overflow-hidden rounded-lg [-webkit-touch-callout:none]", className);
    $[15] = className;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== setIsHovering) {
    t9 = () => setIsHovering(true);
    $[17] = setIsHovering;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== setIsHovering) {
    t10 = () => setIsHovering(false);
    $[19] = setIsHovering;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== children || $[22] !== isHovering || $[23] !== isStatic || $[24] !== lens || $[25] !== mousePosition || $[26] !== position || $[27] !== zoom) {
    t11 = isStatic ? <div><motion.div initial={{
        opacity: 0,
        scale: 0.58
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.8
      }} transition={{
        duration: 0.3,
        ease: "easeOut"
      }} className="absolute inset-0 overflow-hidden" style={{
        maskImage: `radial-gradient(circle ${lens / 2}px at ${position.x}px ${position.y}px, black 100%, transparent 100%)`,
        WebkitMaskImage: `radial-gradient(circle ${lens / 2}px at ${position.x}px ${position.y}px, black 100%, transparent 100%)`,
        transformOrigin: `${position.x}px ${position.y}px`
      }}><div className="pointer-events-none absolute inset-0" style={{
          transform: `scale(${zoom})`,
          transformOrigin: `${position.x}px ${position.y}px`
        }}>{children}</div></motion.div></div> : <AnimatePresence>{isHovering && <div><motion.div initial={{
          opacity: 0,
          scale: 0.58
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.8
        }} transition={{
          duration: 0.3,
          ease: "easeOut"
        }} className="absolute inset-0" style={{
          maskImage: `radial-gradient(circle ${lens / 2}px at ${mousePosition.x}px ${mousePosition.y}px, black 99.999%, transparent 100%)`,
          WebkitMaskImage: `radial-gradient(circle ${lens / 2}px at ${mousePosition.x}px ${mousePosition.y}px, black 99.999%, transparent 100%)`,
          transformOrigin: `${mousePosition.x}px ${mousePosition.y}px`,
          zIndex: 50
        }}><div className="absolute inset-0" style={{
            transform: `scale(${zoom})`,
            transformOrigin: `${mousePosition.x}px ${mousePosition.y}px`
          }}>{children}</div></motion.div></div>}</AnimatePresence>;
    $[21] = children;
    $[22] = isHovering;
    $[23] = isStatic;
    $[24] = lens;
    $[25] = mousePosition;
    $[26] = position;
    $[27] = zoom;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== children || $[30] !== handleMouseMove || $[31] !== props || $[32] !== t10 || $[33] !== t11 || $[34] !== t8 || $[35] !== t9) {
    t12 = <div ref={containerRef} className={t8} onDragStart={t9} onTouchMove={handleMouseMove} onMouseLeave={t10} onMouseMove={handleMouseMove} {...props}>{children}{t11}</div>;
    $[29] = children;
    $[30] = handleMouseMove;
    $[31] = props;
    $[32] = t10;
    $[33] = t11;
    $[34] = t8;
    $[35] = t9;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
};